import { useCallback } from 'react';
import axios from 'axios';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';

/**
 * Profile operations — display name, PIN, and avatar upload (3-step S3).
 * See plan §2.7.
 */
export const useProfile = () => {
  const updateDisplayName = useCallback(async (display_name: string): Promise<void> => {
    await axiosInstance.patch(API_ENDPOINTS.GET_USER_PROFILE, { display_name });
    // keep localStorage user in sync
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...u, display_name }));
    } catch { /* ignore */ }
  }, []);

  const setPin = useCallback(async (pin: string): Promise<void> => {
    await axiosInstance.post(API_ENDPOINTS.SET_PIN, { pin });
  }, []);

  /** Upload avatar: presigned URL -> PUT to S3 -> save path. Returns the saved URL/path. */
  const uploadProfileImage = useCallback(async (file: File): Promise<string> => {
    const ext = (file.type.split('/')[1] || 'jpeg').toLowerCase();
    const { data: urlRes } = await axiosInstance.get(
      `${API_ENDPOINTS.PROFILE_IMAGE_UPLOAD_URL}?file_type=${encodeURIComponent(ext)}`
    );
    const uploadUrl = urlRes?.data?.upload_url;
    const path = urlRes?.data?.path;
    if (!uploadUrl || !path) throw new Error('Could not get upload URL');

    // direct PUT to S3 (bare axios — no auth header / interceptors)
    await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } });

    const { data: saveRes } = await axiosInstance.patch(API_ENDPOINTS.UPDATE_PROFILE_IMAGE, { profile_image_url: path });
    const savedUrl = saveRes?.data?.profile_image_url || path;
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...u, profile_image_url: savedUrl }));
    } catch { /* ignore */ }
    return savedUrl;
  }, []);

  return { updateDisplayName, setPin, uploadProfileImage };
};
